/**
 * useResponsiveDisplayMode Hook - SPEC-019 Task 3.5
 *
 * Detects viewport width and returns appropriate display mode.
 * - < 768px: 'drawer' (mobile)
 * - >= 768px: 'popover' (desktop)
 *
 * @module features/ai-button/hooks/useResponsiveDisplayMode
 * @see SPEC-019 Requirements 2.3, 2.4, 7.1
 */

import { useState, useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export type PanelDisplayMode = 'popover' | 'drawer';

// ============================================================================
// Constants
// ============================================================================

/** Breakpoint for mobile/desktop switch (768px) */
const MOBILE_BREAKPOINT = 767;
const MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useResponsiveDisplayMode Hook
 *
 * Returns the appropriate display mode based on viewport width.
 * Uses matchMedia for efficient viewport monitoring.
 *
 * @returns 'drawer' for mobile (< 768px), 'popover' for desktop (>= 768px)
 *
 * @example
 * ```tsx
 * const displayMode = useResponsiveDisplayMode();
 * // displayMode === 'drawer' on mobile
 * // displayMode === 'popover' on desktop
 * ```
 */
export function useResponsiveDisplayMode(): PanelDisplayMode {
  const [mode, setMode] = useState<PanelDisplayMode>(() => {
    // SSR-safe initial value
    if (typeof window === 'undefined') return 'popover';
    return window.matchMedia(MEDIA_QUERY).matches ? 'drawer' : 'popover';
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MEDIA_QUERY);

    const handleChange = (e: MediaQueryListEvent) => {
      setMode(e.matches ? 'drawer' : 'popover');
    };

    // Set initial value
    setMode(mediaQuery.matches ? 'drawer' : 'popover');

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return mode;
}
